/**
 * VoiceManager - Handles voice loading and selection for speech synthesis
 * Based on the existing getVoiceForCurrentLanguage logic from the prototype
 */
export default class VoiceManager {
	/**
	 * Initialize VoiceManager
	 */
	constructor() {
		this.synth = window.speechSynthesis;
		this.voices = [];
		this.selectedVoice = null;
		this.isLoaded = false;

		// Load voices
		this.loadVoices();
	}

	/**
	 * Load available voices from the browser
	 * @returns {Promise} Promise that resolves when voices are loaded
	 */
	loadVoices() {
		return new Promise((resolve) => {
			let retryCount = 0;
			const maxRetries = 20; // 1 second total wait time (20 * 50ms)
			let voicesChangedHandled = false;

			// Function to get and process voices
			const getVoices = () => {
				this.voices = this.synth.getVoices();

				if (this.voices.length > 0) {
					this.isLoaded = true;
					this.selectedVoice = this.getVoiceForCurrentLanguage();
					resolve(this.voices);
				} else if (retryCount < maxRetries) {
					retryCount++;
					setTimeout(getVoices, 50);
				} else {
					// Timeout reached - resolve with empty array and warn
					console.warn(
						"TitusJS: Voice loading timeout - proceeding without voice selection",
					);
					this.isLoaded = true;
					this.voices = [];
					this.selectedVoice = null;
					resolve([]);
				}
			};

			// Some browsers fire voiceschanged event when voices are ready
			if (this.synth.onvoiceschanged !== undefined) {
				this.synth.onvoiceschanged = () => {
					if (!voicesChangedHandled) {
						voicesChangedHandled = true;
						getVoices();
					}
				};
			}

			// Try to get voices immediately (works in some browsers)
			getVoices();
		});
	}

	/**
	 * Get the best voice for the current user language
	 * Enhanced version of the prototype's getVoiceForCurrentLanguage method
	 * @returns {SpeechSynthesisVoice|null} Selected voice or null
	 */
	getVoiceForCurrentLanguage() {
		if (!this.isLoaded || this.voices.length === 0) {
			console.warn("TitusJS: Voices not yet loaded");
			return null;
		}

		const userLanguage = this.getUserLanguage();
		const languageCode = userLanguage.split("-")[0]; // e.g., 'en' from 'en-US'

		let selectedVoice = null;

		// Priority 1: Exact language match with local preference
		for (const voice of this.voices) {
			if (voice.lang === userLanguage) {
				selectedVoice = voice;
				// Prefer local voices over remote ones
				if (voice.localService) {
					break;
				}
			}
		}

		// Priority 2: Language code match (e.g., 'en' matches 'en-GB', 'en-US')
		if (!selectedVoice) {
			for (const voice of this.voices) {
				if (voice.lang.startsWith(languageCode)) {
					selectedVoice = voice;
					if (voice.localService) {
						break;
					}
				}
			}
		}

		// Priority 3: Default voice
		if (!selectedVoice) {
			selectedVoice =
				this.voices.find((voice) => voice.default) || this.voices[0];
		}

		if (!selectedVoice) {
			console.warn("TitusJS: No suitable voice found");
		}

		return selectedVoice;
	}

	/**
	 * Get the user's language preference
	 * @returns {string} Language code (e.g., 'en-US')
	 */
	getUserLanguage() {
		// Check multiple sources for language preference
		return (
			navigator.language || // Modern browsers
			navigator.userLanguage || // IE
			navigator.browserLanguage || // Older IE
			navigator.systemLanguage || // IE
			"en-US" // Fallback
		);
	}

	/**
	 * Get the currently selected voice
	 * @returns {SpeechSynthesisVoice|null} Currently selected voice
	 */
	getSelectedVoice() {
		return this.selectedVoice;
	}

	/**
	 * Set a specific voice as selected
	 * @param {SpeechSynthesisVoice} voice - Voice to select
	 * @returns {boolean} Whether the voice was successfully selected
	 */
	setSelectedVoice(voice) {
		if (!voice || !this.voices.includes(voice)) {
			console.warn("TitusJS: Invalid voice provided");
			return false;
		}

		this.selectedVoice = voice;
		return true;
	}

	/**
	 * Get voice information for debugging/display
	 * @param {SpeechSynthesisVoice} voice - Voice to get info for
	 * @returns {Object} Voice information object
	 */
	getVoiceInfo(voice) {
		if (!voice) return null;

		return {
			name: voice.name,
			lang: voice.lang,
			localService: voice.localService,
			default: voice.default,
			voiceURI: voice.voiceURI,
		};
	}

	/**
	 * Get information about the current setup
	 * @returns {Object} Status information
	 */
	getStatus() {
		return {
			isLoaded: this.isLoaded,
			voiceCount: this.voices.length,
			selectedVoice: this.selectedVoice
				? this.getVoiceInfo(this.selectedVoice)
				: null,
			userLanguage: this.getUserLanguage(),
			isSupported: "speechSynthesis" in window,
		};
	}

	/**
	 * Force reload of voices (useful if voices change dynamically)
	 * @returns {Promise} Promise that resolves when voices are reloaded
	 */
	async reloadVoices() {
		this.isLoaded = false;
		this.voices = [];
		this.selectedVoice = null;

		await this.loadVoices();
		return this.voices;
	}
}
